import { axiosRequest } from "./AxiosService";
import { WorkFlowEditorType } from "../pages/EditWorkFlow/data/WorkFlowEditorType";
import { WorkflowEditorResponse } from "../type/response/WorkFlowEditorResponse";
import { CreateNewWorkFlowResponse } from "../type/response/CreateNewWorkFlowResponse";

// バックエンド未実装のため一時的に非表示にするアクティビティ名のリスト
// const HIDDEN_NAMES = ["外部サービス実行アクティビティ", "メール送信アクティビティ"];
const HIDDEN_NAMES = ["外部サービス実行アクティビティ"];
/**
 * WorkflowEditorデータ取得API
 * @param applicationFormatId - 申請書ID
 * @returns 整形済みのWorkFlowEditorTypeデータ
 */
export const getWorkflowEditorData = async (
  applicationFormatId: string
): Promise<{ responseData: WorkflowEditorResponse; formattedData: WorkFlowEditorType } | null> => {
  try {
    const response: WorkflowEditorResponse = await axiosRequest(
      "/workflowEditor/editData",
      "POST",
      {
        data: {
          applicationFormatId,
        },
      }
    );

    // 基本的なデータ検証
    if (
      !response ||
      !response.application || !Array.isArray(response.applicationActivities)) {
      console.warn("不正なレスポンス形式です:", response);
      return null;
    }

    const formatted: WorkFlowEditorType = {
      applicationFormatId: response.application.id,
      type: {
        id: response.applicationType.id,
        typeName: response.applicationType.name
      },
      code: response.application.code,
      version: response.application.version,
      name: response.application.name,
      description: response.application.description,
      isDeleted: response.application.isDeleted,
      isDraft:response.application.isDraft,
      appActivityList: response.applicationActivities.map(AppActivity => ({
        applicationActivityId: AppActivity.id,
        order: AppActivity.activityOrder,
        appActivityName: AppActivity.activityName,
        defaultConfig: AppActivity.defaultConfig,
        mailFormat: AppActivity.mailFormat ? {
          id: AppActivity.mailFormat.id,
          subject: AppActivity.mailFormat.subject,
          body: AppActivity.mailFormat.body,
          toUsers: AppActivity.mailFormat.toUsers?.map(u => ({ id: u.id, firstName: u.firstName, lastName: u.lastName }))
        } : undefined,
        reporterList: AppActivity.reporterList ?? [],
        template: {
          id: AppActivity.template.id,
          templateName: AppActivity.template.templateName,
          code: AppActivity.template.code
        }
      })),
      selectTypeList: response.selectTypeList.map(type => ({
        id: type.id,
        typeName: type.name
      })),
      selectTemplateList: response.selectTemplateList
        .filter(template => !HIDDEN_NAMES.includes(template.templateName)) // バックエンド未実装のため一時的に非表示
        .map(template => ({
          id: template.id,
          templateName: template.templateName,
          code: template.code
        })),
      selectMailTemplateList: response.selectMailTemplateList.map(mailTmp => ({
        id: mailTmp.id,
        name: mailTmp.name,
        description: mailTmp.description,
        subject: mailTmp.subject,
        body: mailTmp.body,
        toUsers: mailTmp.toUsers?.map((v) => ({ id: v.id, firstName: v.firstName, lastName: v.lastName }))
      })),
      selectApproverList: response.selectUserList.map(user => ({
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName
      })),
      selectReporterList: response.selectUserList.map(user => ({
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName
      })),
      selectToUserList: response.selectUserList.map(user => ({
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName
      })),
      formItems: response.formItems.map(item => ({
        formId: item.id,
        itemOrder: item.itemOrder,
        label: item.label,
        type: item.type,
        isRequired: item.isRequired,
        defaultValue: item.defaultValue,
        selectItemList: item.selectItemList?.map(select => ({
          id: select.id,
          selectOrder: select.selectOrder,
          value: select.value,
          label: select.label
        }))
      }))
    };
    return { responseData: response, formattedData: formatted };
  } catch (error) {
    console.error("ワークフローデータの取得に失敗しました:", error);
    return null;
  }
};

/**
 * 新規ワークフロー作成用の初期データ取得API
 * @returns 整形済みのWorkFlowEditorTypeデータ（初期値）、失敗時はnull
 */
export const getCreateNewWorkflowData = async (): Promise<WorkFlowEditorType | null> => {
  try {
    const response: CreateNewWorkFlowResponse = await axiosRequest(
      "/workflowEditor/initData",
      "GET"
    );

    if (!response || !Array.isArray(response.selectTypeList)) {
      console.warn("新規workflowの初期データ取得に失敗しました:", response);
      return null;
    }

    const formatted: WorkFlowEditorType = {
      applicationFormatId: "",
      type: {
        id: "",
        typeName: ""
      },
      code: "",
      version: "",
      name: "",
      description: "",
      isDeleted: false,
      isDraft: false,
      appActivityList: [],
      selectTypeList: response.selectTypeList.map(type => ({
        id: type.id,
        typeName: type.name
      })),
      selectTemplateList: response.selectTemplateList
        .filter(template => !HIDDEN_NAMES.includes(template.templateName)) // バックエンド未実装のため一時的に非表示
        .map(template => ({
          id: template.id,
          templateName: template.templateName,
          code: template.code
        })),
      selectMailTemplateList: response.selectMailTemplateList.map(mailTmp => ({
        id: mailTmp.id,
        name: mailTmp.name,
        description: mailTmp.description,
        subject: mailTmp.subject,
        body: mailTmp.body,
        toUsers: mailTmp.toUsers?.map((v) => ({ id: v.id, firstName: v.firstName, lastName: v.lastName }))
      })),
      selectApproverList: response.selectUserList.map(user => ({
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName
      })),
      selectReporterList: response.selectUserList.map(user => ({
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName
      })),
      selectToUserList: response.selectUserList.map(user => ({
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName
      })),
      formItems: [],
    };

    return formatted;
  } catch (error) {
    console.error("新規workflowデータの取得に失敗しました:", error);
    return null;
  }
};


